import { Boxes, FileText, History, ListOrdered } from "lucide-react";
import { type SyntheticEvent, useState } from "react";
import { SaveButtonField, TextField } from "../../web/components/fields";
import { RecordWindow } from "../../web/components/RecordWindow";
import { CheckedPill } from "./CheckedMark";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { useConfirm, useWindowConfirm } from "../../web/components/Confirm";
import { archiveAction } from "../../web/components/recordActions";
import { SyncSelect } from "../../web/components/SyncSelect";
import { Box, ErrorNotice, IssueChip, Issues, Notice } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { useMoney } from "../../web/lib/money";
import { nextShowToken } from "../../web/lib/flash";
import { useFreshRecord } from "../../web/lib/queries";
import { useNewUuid } from "../../web/lib/newUuid";
import { can, itemName, usePriceListSaver, whyNot } from "./data";
import { oneOnlyClash, tagIdsOf } from "./smartTags";
import { sameTags, TagPicker } from "./TagPicker";
import { SmartNameNotice, TagClashNotice } from "./NameTags";
import { composeName, splitName } from "./names";
import { usePriceListWindows, useUnsavedGuard } from "./windows";
import { type BundleRow, bundleNumber, ITEM_NUMBER_MAX, isProduct, type PriceListData } from "./meta";
import { ChangedNotice, HistoryTab } from "./HistoryTab";
import { type ItemProblemId, bundleItemAttention, bundleMakesTooLittle, bundleMarkup, bundleNumberProblem, itemContext, sharedNumberText } from "./attention";
import { ProblemChips, problemKinds } from "./ProblemChips";
import { itemNumberIssues } from "./setup";
import { DuplicatePopup, duplicateAction } from "./DuplicatePopup";
import { Contents, itemChoices } from "./Contents";

/*
 * One bundle, in a window like an item's: Details (its name and item number) and Contents (the items in it, how many of each, their
 * prices and what they add up to: where it's priced). Fields save as you leave them.
 */

const TABS = [
    { id: "details", label: "Details", icon: <FileText className="size-5" /> },
    {
        id: "contents",
        label: "Contents",
        icon: <ListOrdered className="size-5" />,
    },
    { id: "history", label: "History", icon: <History className="size-5" /> },
];

const itemNumberCheck = (v: string) => {
    if (v.length > ITEM_NUMBER_MAX) return `At most ${ITEM_NUMBER_MAX} characters`;
    return v ? null : "ServiceM8 needs an item number for a bundle";
};

export function BundleWindow(
    props: Readonly<{
        data: PriceListData;
        uuid: string;
        tab: string;
        onTab: (tab: string) => void;
        onClose: () => void;
        onOpenItem: (uuid: string) => void;
    }>,
) {
    const company = useCompany();
    const saver = usePriceListSaver();
    const b = props.data.bundles.find((x) => x.uuid === props.uuid);
    useFreshRecord(company.id, "materialbundle", b ? props.uuid : null);
    const [error, setError] = useState<Error | null>(null);
    // Duplicating it: its popup open (DuplicatePopup), and the copy's uuid (the same for every try).
    const [duplicating, setDuplicating] = useState(false);
    const copyUuid = useNewUuid();
    const windows = usePriceListWindows();
    // The item number warning's button takes you to the field, every time it's pressed.
    const [numberFocus, setNumberFocus] = useState(0);
    // Its tags being edited (TagPicker), kept while other tabs are open; null: not being edited. Closing with them unsaved asks first.
    const [tagsDraft, setTagsDraft] = useState<string[] | null>(null);
    const windowConfirm = useWindowConfirm();
    const tagsUnsaved = b !== undefined && tagsDraft !== null && !sameTags(tagsDraft, props.data.itemTags[b.uuid] ?? []);
    // A new item number typed and not saved (it saves only with its Save button, as an item's): kept while other tabs are open.
    const [numberDraft, setNumberDraft] = useState<string | null>(null);
    // Its contents narrowed to the items with one kind of warning (its chip pressed), or null: all.
    const [only, setOnly] = useState<ItemProblemId | null>(null);
    // Another bundle opened here: its drafts started afresh in the same render (as an item's window does).
    const [draftsOf, setDraftsOf] = useState(props.uuid);
    if (draftsOf !== props.uuid) {
        setDraftsOf(props.uuid);
        setTagsDraft(null);
        setNumberDraft(null);
        setOnly(null);
    }
    // What leaving it would lose ("its tags and its new item number").
    const unsaved = [tagsUnsaved ? "its tags" : "", numberDraft !== null ? "its new item number" : ""].filter(Boolean).join(" and ");
    // Ask before leaving them (closing, or opening another bundle here), with them in view behind it.
    const ask = (leave: () => void, label: string) => {
        props.onTab("details");
        void windowConfirm
            .confirm({ title: "Leave without saving?", message: `Not saved: ${unsaved}.`, confirmLabel: label, cancelLabel: "Keep editing", tone: "danger" })
            .then((yes) => yes && leave());
    };
    useUnsavedGuard("bundle", unsaved !== "", (proceed) => ask(proceed, "Discard and open the other bundle"));
    const goToNumber = () => {
        props.onTab("details");
        setNumberFocus(nextShowToken());
    };

    if (!b) {
        return (
            <RecordWindow kind="bundle" tone="copper" icon={<Boxes className="size-4" />} title="Bundle not found" tabs={[]} tab="" onTab={() => undefined} onClose={props.onClose}>
                <Notice>This bundle doesn't exist any more.</Notice>
            </RecordWindow>
        );
    }
    const canEdit = can(props.data, "bundles");
    const tab = TABS.some((t) => t.id === props.tab) ? props.tab : "details";
    const setActive = (active: boolean) => {
        setError(null);
        saver.updateBundle(b.uuid, { active }).catch((err: Error) => setError(err));
    };

    return (
        <RecordWindow
            kind="bundle"
            share
            tone="copper"
            icon={<Boxes className="size-4" />}
            title={<span className="truncate">{b.name || "Untitled bundle"}</span>}
            label={b.name || "Untitled bundle"}
            subtitle={b.item_number || undefined}
            barExtra={b.active ? <CheckedPill data={props.data} kind="materialbundle" id={b.uuid} onError={setError} /> : null}
            tabs={TABS.map((t) => ({ ...t, unsaved: t.id === "details" && unsaved !== "", count: t.id === "contents" ? b.material_list.length : undefined }))}
            tab={tab}
            onTab={props.onTab}
            onClose={() => (unsaved ? ask(props.onClose, "Discard and close") : props.onClose())}
            actions={[
                canEdit && duplicateAction("bundle", () => setDuplicating(true)),
                canEdit && archiveAction("bundle", b.name || "Untitled bundle", b.active, setActive),
            ]}
            notices={
                <>
                    <windowConfirm.Here />
                    <ErrorNotice error={error} />
                    {/* Its problems as one box, worst first. */}
                    <Issues
                        chips={
                            <BundleIssueChips
                                data={props.data}
                                bundle={b}
                                onShow={(kind) => {
                                    setOnly(kind);
                                    props.onTab("contents");
                                }}
                            />
                        }
                    >
                        <BundleNumberNotice data={props.data} bundle={b} onDetails={canEdit ? goToNumber : undefined} />
                        <ChangedNotice object="materialbundle" uuid={b.uuid} data={props.data} />
                    </Issues>
                    {b.active ? null : <Notice tone="warning">This bundle is archived: it's hidden in ServiceM8 and can't be added to jobs.</Notice>}
                    {whyNot(props.data, "bundles") ? <Notice>{whyNot(props.data, "bundles")}</Notice> : null}
                </>
            }
        >
            <>
                {tab === "details" && (
                    <Box>
                        {/* Its name without its tags' wording (as an item's): its tags (below) put that in. */}
                        <TextField
                            label="Name"
                            value={splitName(b.name, props.data.nameTags).base}
                            disabled={!canEdit}
                            validate={(v) => (v.trim() ? null : "Name is required")}
                            onSave={(base) =>
                                saver.updateBundle(b.uuid, {
                                    name: composeName(
                                        {
                                            ...splitName(b.name, props.data.nameTags),
                                            base: base.trim(),
                                        },
                                        props.data.nameTags,
                                    ),
                                })
                            }
                        />
                        <BundleNumberField key={`number:${b.uuid}`} data={props.data} bundle={b} canEdit={canEdit} focusToken={numberFocus || undefined} draft={numberDraft} onDraft={setNumberDraft} />
                        {/* Its tags, and the full name they make (its warnings under them), as an item's. */}
                        <TagPicker
                            key={`tags:${b.uuid}`}
                            data={props.data}
                            item={b}
                            bundle
                            fix={
                                b.active ? (
                                    <>
                                        {oneOnlyClash(props.data, b.uuid) ? <TagClashNotice text={`More than one ${oneOnlyClash(props.data, b.uuid)?.name ?? ""} tag`} /> : null}
                                        <SmartNameNotice data={props.data} item={b} bundle />
                                    </>
                                ) : undefined
                            }
                            draft={tagsDraft}
                            onDraft={setTagsDraft}
                            suggested={itemsTags(props.data, b)}
                        />
                    </Box>
                )}
                <LeaveGuard window={["bundle", b.uuid]} when={unsaved !== ""} message={`${b.name || "Untitled bundle"}: not saved, ${unsaved}.`} />
                {tab === "contents" && (
                    <Contents
                        data={props.data}
                        of={{
                            kind: "bundle",
                            uuid: b.uuid,
                            name: b.name,
                            lines: b.material_list,
                            canEdit,
                            save: (material_list) => saver.updateBundle(b.uuid, { material_list }),
                        }}
                        onOpenItem={props.onOpenItem}
                        only={only}
                        onOnly={setOnly}
                    />
                )}
                {tab === "history" && <HistoryTab object="materialbundle" uuid={b.uuid} data={props.data} />}
                {duplicating ? (
                    <DuplicatePopup
                        data={props.data}
                        kind="bundle"
                        name={b.name}
                        withNumber
                        supplierPrices={null}
                        // Its items and quantities.
                        onCreate={(copy) =>
                            saver
                                .createBundle({
                                    name: copy.name,
                                    item_number: copy.itemNumber,
                                    material_list: b.material_list,
                                    uuid: copyUuid.get(),
                                })
                                .then((made) => {
                                    copyUuid.made();
                                    windows.openBundle(made.uuid);
                                })
                        }
                        onClose={() => setDuplicating(false)}
                    />
                ) : null}
            </>
        </RecordWindow>
    );
}

/**
 * Its item number: it matters (jobs add it by it), so it saves only with Save, and changing the one it has is asked first, held (as
 * an item's; its first one isn't asked). Inside its window, so the question covers just the window.
 */
function BundleNumberField(
    props: Readonly<{ data: PriceListData; bundle: BundleRow; canEdit: boolean; focusToken?: number; draft: string | null; onDraft: (draft: string | null) => void }>,
) {
    const { data, bundle: b } = props;
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const confirmNumber = (next: string) => {
        if (!b.item_number) return Promise.resolve(true);
        return confirm({
            title: "Change its item number?",
            message: (
                <>
                    {b.item_number} → <span className="font-semibold">{next}</span>
                </>
            ),
            confirmLabel: "Change item number",
            hold: true,
        });
    };
    return (
        <SaveButtonField
            label="Item number"
            value={b.item_number}
            disabled={!props.canEdit}
            validate={itemNumberCheck}
            hint={<NumberHint data={data} bundle={b} canEdit={props.canEdit} confirm={confirmNumber} />}
            focusToken={props.focusToken}
            warning={bundleNumberProblem(b, itemNumberIssues([...data.materials.filter((m) => !isProduct(m)), ...data.bundles]))}
            draft={props.draft}
            onDraft={props.onDraft}
            confirm={confirmNumber}
            onSave={(item_number) => saver.updateBundle(b.uuid, { item_number })}
            className="max-w-sm"
        />
    );
}

/** Its item number's hint, and (asked for, never by itself) the number its items' give it: "CYL570-LABCYL" (meta.ts bundleNumber). */
function NumberHint({
    data,
    bundle,
    canEdit,
    confirm,
}: Readonly<{
    data: PriceListData;
    bundle: BundleRow;
    canEdit: boolean;
    /** Asked first, as typing one is. */ confirm: (next: string) => Promise<boolean>;
}>) {
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    const suggested = bundleNumber(data.materials, bundle);
    return (
        <>
            Unique across all items and bundles. Used to add it to jobs.
            {suggested && canEdit ? (
                <>
                    {" "}
                    <button
                        type="button"
                        className="link-button"
                        title={`Change it to ${suggested}`}
                        onClick={() =>
                            void confirm(suggested)
                                .then((yes) =>
                                    yes
                                        ? saver.updateBundle(bundle.uuid, {
                                              item_number: suggested,
                                          })
                                        : null,
                                )
                                .catch((err: Error) => setError(err))
                        }
                    >
                        Use {suggested}
                    </button>
                </>
            ) : null}
            {error ? <span className="block text-danger">{error.message}</span> : null}
        </>
    );
}

/** Its item number: none, or shared with an item or another bundle (accounting software matches invoice lines by it). */
function BundleNumberNotice({
    data,
    bundle,
    onDetails,
}: Readonly<{
    data: PriceListData;
    bundle: BundleRow;
    onDetails?: () => void;
}>) {
    const { missing, clashes } = itemNumberIssues([...data.materials.filter((m) => !isProduct(m)), ...data.bundles]);
    const others = clashes.get(bundle.uuid);
    if (!bundle.active || (!missing.has(bundle.uuid) && !others)) return null;
    return (
        <Notice
            tone="warning"
            title={others ? "Shared item number." : "No item number."}
            actions={
                onDetails ? (
                    <button type="button" className="btn-secondary" onClick={onDetails}>
                        {others ? "Change item number" : "Add item number"}
                    </button>
                ) : null
            }
        >
            {others ? sharedNumberText(others, "bundle") : "Every bundle needs one."}
        </Notice>
    );
}

/**
 * Along the top of its "Needs attention" box, as an item's: making too little (red: its markup under the minimum, or nothing, or a
 * loss; with See costs), then its items' warnings (attention.ts bundleItemAttention: every one an item has), one chip for each kind
 * with how many items have it ("2 Price to update"), which on hover. Each shows its contents narrowed to those items (`onShow`, its
 * kind; making too little: all of them), to open each one and deal with it.
 */
function BundleIssueChips({ data, bundle, onShow }: Readonly<{ data: PriceListData; bundle: BundleRow; onShow: (kind: ItemProblemId | null) => void }>) {
    const fmt = useMoney();
    const seesCosts = can(data, "costs.view");
    const ctx = itemContext(data);
    if (!bundle.active) return null;
    const kinds = problemKinds(bundleItemAttention(bundle, ctx, seesCosts), (uuid) => {
        const m = ctx.items.get(uuid);
        return m ? itemName(data, m) : "An item";
    });
    const low = seesCosts && bundleMakesTooLittle(bundle, ctx);
    const markup = bundleMarkup(bundle, ctx) ?? 0;
    const min = data.settings.minMarkup;
    return (
        <>
            {low ? (
                <IssueChip
                    tone="error"
                    label="Making too little"
                    title={min > 0 ? `Its items add up to a ${fmt.percent(Math.round(markup))} markup, under your ${fmt.percent(min)} minimum.` : "Its items' prices add up to no more than their costs."}
                    onShow={() => onShow(null)}
                />
            ) : null}
            <ProblemChips kinds={kinds} onShow={onShow} />
        </>
    );
}

/** Its items' tags, the most used first (ties as its items are listed): what its tags are likely to be. */
function itemsTags(data: PriceListData, bundle: BundleRow) {
    const counts = new Map<string, number>();
    for (const l of bundle.material_list) for (const id of tagIdsOf(data, l.uuid)) counts.set(id, (counts.get(id) ?? 0) + 1);
    return [...counts].sort((a, b) => b[1] - a[1]).map(([id]) => id);
}

/** Adding a bundle: its name, item number and first item (ServiceM8 needs at least one). */
export function NewBundleWindow(
    props: Readonly<{
        data: PriceListData;
        onCreated: (uuid: string) => void;
        onClose: () => void;
    }>,
) {
    const saver = usePriceListSaver();
    const [name, setName] = useState("");
    const [itemNumber, setItemNumber] = useState("");
    const [first, setFirst] = useState("");
    // Its uuid, the same for every try (a retry after a timeout can't make a second).
    const newUuid = useNewUuid();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const ready = props.data.bundlesUnavailable === null && name.trim() && itemNumber.trim() && first;
    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!ready) return;
        setBusy(true);
        setError(null);
        saver
            .createBundle({
                name: name.trim(),
                item_number: itemNumber.trim(),
                material_list: [{ uuid: first, quantity: 1 }],
                uuid: newUuid.get(),
            })
            .then((b) => {
                newUuid.made();
                props.onCreated(b.uuid);
            })
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    return (
        <RecordWindow
            kind="bundle"
            tone="copper"
            icon={<Boxes className="size-4" />}
            title="New bundle"
            tabs={[
                {
                    id: "details",
                    label: "Details",
                    icon: <FileText className="size-5" />,
                },
            ]}
            tab="details"
            onTab={() => undefined}
            onClose={props.onClose}
        >
            <form className="mx-auto w-full max-w-lg space-y-4" onSubmit={submit}>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Name</span>
                    <input className="input text-sm" value={name} onChange={(e) => setName(e.target.value)} required />
                </label>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Item number</span>
                    <input className="input text-sm" value={itemNumber} maxLength={ITEM_NUMBER_MAX} onChange={(e) => setItemNumber(e.target.value)} required />
                </label>
                <div className="space-y-1">
                    <span className="text-xs font-medium text-muted">First item</span>
                    <SyncSelect label="First item" placeholder="Choose an item..." options={itemChoices(props.data, [], false)} value={first} onChange={setFirst} />
                </div>
                {props.data.bundlesUnavailable ? <Notice>{props.data.bundlesUnavailable}</Notice> : null}
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !ready}>
                        Add bundle
                    </button>
                </div>
            </form>
        </RecordWindow>
    );
}
